import React, { createContext, useState, useContext, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
// Faz 11 Cephe 3 (2026-06-03) — ROLE_MODULES test-edilebilirlik için ayrı
// constants modülünde duruyor; AuthContext re-export ediyor (backward-compatible
// `import { ROLE_MODULES } from '...AuthContext'` çalışmaya devam eder).
import { ROLE_MODULES } from '../constants/roleModules';
// İt360 (2026-06-08) — Karargah onaylı clearAuthCache entegrasyonu.
// login/logout/role değişim noktalarına bağlandı: yetki anında etkin,
// F5 (sayfa yenileme) gerek yok.
import { clearAuthCache, watchAuthVersion } from '../utils/cacheInvalidation';
export { ROLE_MODULES };

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionKicked, setSessionKicked] = useState(false);

  useEffect(() => {
    // Check for existing session on mount
    const checkAuth = () => {
      try {
        const token = localStorage.getItem('token');
        const savedUser = localStorage.getItem('user');

        if (token && savedUser) {
          const userData = JSON.parse(savedUser);
          setUser(userData);
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          console.log('✅ Session restored:', userData.email, 'Role:', userData.role);
        } else {
          console.log('⚠️ No existing session found');
        }
      } catch (error) {
        console.error('Error restoring session:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();

    // Response Interceptor: 401 logout + kullanıcı dostu hata mesajları
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && !error.config?._isRefresh) {
          const detail = error.response?.data?.detail || '';
          // FIX 2026-04-28: REAUTH_REQUIRED logout etmemeli — DeveloperDocEditor şifre modal'ı gösterir.
          const isReauthChallenge = typeof detail === 'string' && detail.includes('REAUTH_REQUIRED');
          if (!isReauthChallenge) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            delete axios.defaults.headers.common['Authorization'];
            setUser(null);
            if (detail === 'SESSION_REPLACED') {
              console.log('⚠️ Başka bir cihazdan giriş yapıldı - oturum sonlandırıldı');
              setSessionKicked(true);
            } else {
              console.log('⚠️ Token expired - otomatik çıkış yapıldı');
            }
          }
        }
        // FIX 2026-04-28: Backend bazı endpoint'lerde structured detail (dict) döndürür
        // (fixable+fields için). Frontend yanlışlıkla bu dict'i React child olarak
        // render ederse "Minified React error #31" patlar. Burada detail'i string'e
        // normalize ederiz; orijinal dict `detail_obj`'a kopyalanır (fixable check için).
        if (error.response?.data && error.response.data.detail !== undefined && error.response.data.detail !== null) {
          const d = error.response.data.detail;
          if (typeof d === 'object') {
            error.response.data.detail_obj = d;
            error.response.data.detail = (typeof d.message === 'string' ? d.message : JSON.stringify(d)).slice(0, 600);
          }
        }
        // Kullanıcı dostu hata mesajı oluştur (network/server hataları için)
        if (!error.response) {
          error.userMessage = 'Sunucuya bağlanılamadı. İnternet bağlantınızı kontrol edin.';
        } else if (error.response.status === 500) {
          error.userMessage = error.response.data?.detail || 'Sunucu hatası oluştu. Lütfen tekrar deneyin.';
        } else if (error.response.status === 502 || error.response.status === 503) {
          error.userMessage = 'Sunucu geçici olarak kullanılamıyor. Lütfen birkaç dakika sonra tekrar deneyin.';
        } else if (error.response.status === 408 || error.code === 'ECONNABORTED') {
          error.userMessage = 'İstek zaman aşımına uğradı. Lütfen tekrar deneyin.';
        }
        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  // Silent token refresh — her 20 dakikada token yenile (JWT 24 saat)
  // 🔒 PostHog rrweb session recording — sadece operasyonel rolde aktif. Client'ta KAPALI.
  useEffect(() => {
    if (!user || typeof window === 'undefined' || !window.posthog) return;
    const OP_ROLES = ['SuperAdmin','Developer','Manager','Supervisor','Admin','Picker','Packer','Shipper','Controller','DepotManager','InboundOperator'];
    try {
      if (OP_ROLES.includes(user.role)) {
        // Operasyonel rol: kayıt başlat (zaten maskAllInputs/maskTextSelector aktif)
        window.posthog.startSessionRecording?.();
      } else {
        // Client veya diğer roller: kesinlikle kapat
        window.posthog.stopSessionRecording?.();
      }
    } catch (_) { /* silent */ }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const refreshInterval = setInterval(async () => {
      try {
        const resp = await axios.post(`${API_URL}/api/auth/refresh`, {}, { _isRefresh: true });
        const { access_token, user: userData } = resp.data;
        localStorage.setItem('token', access_token);
        localStorage.setItem('user', JSON.stringify(userData));
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        setUser(userData);
      } catch (err) {
        console.warn('Token refresh başarısız:', err.message);
      }
    }, 20 * 60 * 1000); // 20 dakika
    return () => clearInterval(refreshInterval);
  }, [user]);

  // FIX 2026-04-27: Tab fokus alınca user objesini hafif yenile (extra_permissions vs. değişmiş olabilir)
  // Refresh interval (20dk) çok uzun; developer yetki verince anında yansıması için.
  useEffect(() => {
    if (!user) return;
    let lastFocusFetch = 0;
    const onFocus = async () => {
      const now = Date.now();
      if (now - lastFocusFetch < 60000) return; // 60sn içinde tekrar fetch etme (rate-limit)
      lastFocusFetch = now;
      try {
        const resp = await axios.get(`${API_URL}/api/auth/me`);
        const fresh = resp.data;
        // Sadece extra_permissions/role/active değişmişse setUser
        const cur = user;
        const grantsChanged = JSON.stringify(fresh.extra_permissions || []) !== JSON.stringify(cur.extra_permissions || []);
        const roleChanged = fresh.role !== cur.role;
        if (grantsChanged || roleChanged) {
          const merged = { ...cur, ...fresh };
          localStorage.setItem('user', JSON.stringify(merged));
          setUser(merged);
          // İt360 — Yetki değişimi anında etkin: stale cache imha + storage event yay
          clearAuthCache(roleChanged ? 'role_changed_focus' : 'grants_changed_focus');
        }
      } catch (_) { /* silent */ }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [user]);

  // İt360 — Diğer sekmede yetki değişti ise mevcut sekme React state'ini yenile.
  useEffect(() => {
    const cleanup = watchAuthVersion(async () => {
      // auth-version bump algılandı → /me'den taze user al + setUser
      try {
        const resp = await axios.get(`${API_URL}/api/auth/me`);
        const fresh = resp.data;
        if (fresh) {
          localStorage.setItem('user', JSON.stringify(fresh));
          setUser(fresh);
        }
      } catch (_) { /* silent */ }
    });
    return cleanup;
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      // FIX 2026-05-15 (Defansif destructure): response.data shape değişirse veya backend boş
      // gövde dönerse `|| {}` ile crash-yerine-graceful; ardından access_token explicit kontrol.
      const { access_token, user: userData } = response.data || {};
      if (!access_token || !userData) {
        return { success: false, error: 'Sunucudan beklenen oturum verisi gelmedi (access_token veya user eksik)' };
      }

      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      setUser(userData);
      // İt360 — Login: temiz oturum, eski user cache imha + version bump
      clearAuthCache('login');
      return { success: true };
    } catch (error) {
      const detail = error.response?.data?.detail;
      let msg;
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail)) {
        // FastAPI validation error: [{loc, msg, type}, ...]
        msg = detail.map(d => (typeof d === 'string' ? d : (d?.msg || JSON.stringify(d)))).join(', ');
      } else if (detail && typeof detail === 'object') {
        msg = detail.msg || JSON.stringify(detail);
      } else {
        msg = error.userMessage || error.message || 'Login failed';
      }
      return { success: false, error: msg };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    // İt360 — Logout: tüm auth/cache imha + storage event yay
    clearAuthCache('logout');
  }, []);

  // Developer = full bypass: tüm modül + tüm rol kontrolü TRUE.
  // Ek: extra_permissions içinde "module:<X>" grant'ı varsa o modüle erişim açılır.
  const canAccessModule = useCallback((module) => {
    if (!user) return false;
    if (user.role === 'Developer') return true;
    const allowedModules = ROLE_MODULES[user.role] || [];
    if (allowedModules.includes(module)) return true;
    const grants = user.extra_permissions || [];
    return grants.includes(`module:${module}`);
  }, [user]);

  const hasRole = useCallback((...roles) => {
    if (!user) return false;
    if (user.role === 'Developer') return true; // Developer her rol gibi davranabilir
    return roles.includes(user.role);
  }, [user]);

  const isSuperAdmin = useCallback(() => {
    return user?.role === 'SuperAdmin' || user?.role === 'Developer';
  }, [user]);

  // Developer rolü — Berkant'ın super-user yetkisi
  const isDeveloper = useCallback(() => {
    return user?.role === 'Developer';
  }, [user]);

  // FIX 2026-04-29: Override / Developer Doc Editor / sipariş silme gibi tehlikeli yetkiler
  // sadece Developer rolünde — SuperAdmin'den çıkarıldı. (Önceden SuperAdmin'e de açıktı,
  // backend Developer-only'a çevrildi, frontend de hizalandı.)
  const isSuperUser = useCallback(() => {
    return user?.role === 'Developer';
  }, [user]);

  const updateUser = useCallback((userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  }, []);

  const contextValue = useMemo(() => ({
    user,
    setUser: updateUser,
    login,
    logout,
    loading,
    canAccessModule,
    hasRole,
    isSuperAdmin,
    isDeveloper,
    isSuperUser
  }), [user, updateUser, login, logout, loading, canAccessModule, hasRole, isSuperAdmin, isDeveloper, isSuperUser]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}

      {/* Oturum sonlandırma modalı */}
      {sessionKicked && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 99999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: '32px 28px', maxWidth: 400, width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)', textAlign: 'center'
          }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%', margin: '0 auto 16px',
              background: '#FEF2F2', display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                <path d="M12 9v4" />
                <path d="M12 17h.01" />
              </svg>
            </div>
            <h2 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: '#1E293B' }}>
              Oturum Sonlandirildi
            </h2>
            <p style={{ margin: '0 0 24px', fontSize: 14, color: '#64748B', lineHeight: 1.5 }}>
              Baska bir cihazdan giris yapildigi icin bu oturumunuz sonlandirildi.
              Devam etmek icin tekrar giris yapin.
            </p>
            <button
              onClick={() => { setSessionKicked(false); window.location.href = '/login'; }}
              style={{
                width: '100%', padding: '12px 24px', border: 'none', borderRadius: 10,
                background: '#2563EB', color: '#fff', fontSize: 15, fontWeight: 600,
                cursor: 'pointer', transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = '#1D4ED8'}
              onMouseLeave={(e) => e.target.style.background = '#2563EB'}
            >
              Giris Sayfasina Git
            </button>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
